import React, { useEffect, useRef, useState } from 'react'
import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { emailSessionPath } from 'components/routes/api'
import { api } from 'components/api'
import HeaderIcon from 'images/authentication_modals/email.svg'
import { claimYourPagePath } from 'components/routes/path'

type RedirectPath = string | null

interface EmailSessionResponse {
  status: string
  redirectPath: RedirectPath
}

interface ApiError {
  status: number
  message?: string
}

interface SubmitEmailParams {
  email: string
  overrideRestrictedDomain: boolean
}

async function submitEmail (params: SubmitEmailParams): Promise<EmailSessionResponse> {
  const res = await api.postWithCsrf({
    url: emailSessionPath(),
    data: {
      email: params.email,
      overrideRestrictedDomain: params.overrideRestrictedDomain
    }
  })

  return res.data as EmailSessionResponse
}

function EmailOnlyForm (): React.ReactElement {
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [email, setEmail] = useState('')
  const [redirectPath, setRedirectPath] = useState<RedirectPath>(null)
  const [error, setError] = useState<boolean>(false)

  const defaultErrorMessage = "We're sorry there was an error"
  const rejectedErrorMessage = 'The account associated with this email is not permitted to write reviews. Please contact support@procurated.com if you believe you have received this message in error.'

  const [errorMessage, setErrorMessage] = useState<string>(defaultErrorMessage)
  const [showRestrictedDomainAlert, setShowRestrictedDomainAlert] = useState<boolean>(false)

  useEffect(() => {
    emailInputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (showRestrictedDomainAlert) {
      setShowRestrictedDomainAlert(false)
    }
  }, [email])

  useEffect(() => {
    if (redirectPath == null) { return }
    const timeout = setTimeout(() => {
      window.location.href = redirectPath
    }, 1000)

    return () => clearTimeout(timeout) // clear timeout on unmount
  }, [redirectPath])

  // Explicit generics: <TData, TError, TVariables>
  const mutation: UseMutationResult<EmailSessionResponse, unknown, SubmitEmailParams> = useMutation({
    mutationFn: submitEmail,
    onSuccess: (data) => {
      setError(false)
      const redirectPath = data.redirectPath
      setRedirectPath(redirectPath)
      setEmail('')
    },
    onError: (err: unknown) => {
      const apiError = err as ApiError
      handleSetErrorMessage(apiError.status)
    }
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    mutation.mutate({
      email,
      overrideRestrictedDomain: showRestrictedDomainAlert
    })
  }

  const handleSetErrorMessage = (status: number): void => {
    if (status === 403) {
      setRedirectPath(null)
      setErrorMessage(rejectedErrorMessage)
      setError(true)
    } else if (status === 418) {
      setShowRestrictedDomainAlert(true)
    } else {
      setRedirectPath(null)
      setErrorMessage(defaultErrorMessage)
      setError(true)
    }
  }

  if (redirectPath != null) {
    return <p>Your email has been submitted! ... you will be forwarded</p>
  }

  if (error) {
    return <p>{errorMessage}</p>
  }

  const isSubmitting = mutation.isPending

  return (
    <div className='email-only-modal focusable'>
      <form id='auth-email-only-form' onSubmit={handleSubmit}>
        <div className='header-container'>
          <img src={HeaderIcon} alt='Enter Email' className='header-icon' />
          <div className='header pro-head-5' role='heading'>
            Enter Email
          </div>
        </div>

        <p className='pro-text-sm'><strong>Only public sector employees can publish reviews on Procurated.</strong></p>

        <label htmlFor='email'>Email</label>
        <input
          ref={emailInputRef}
          aria-label='email'
          name='email'
          type='email'
          placeholder='name@example.com'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />

        {showRestrictedDomainAlert
          ? (
            <div>
              <p className='pro-text-xs color-pro-red-500'>
                Please use a professional email address so we can confirm that you are a public sector employee.
              </p>
              <p className='pro-text-xs color-pro-red-500'>
                You can submit your review with the email address provided, but it will not be published until
                an administrator is able to contact you and confirm your identity.
              </p>
            </div>
            )
          : (
            <p className='pro-text-xs'>
              Please enter your professional email address to help us verify your account and publish your review as quickly as possible.
              Reviews written by suppliers or with personal email addresses will not be published.
              If you are a supplier interested in reading reviews written by your public sector customers, you can
              <a href={claimYourPagePath()} className='pro-text-xs'> claim your page </a>
              to get access to our supplier site.
            </p>
            )}
        <button className='button float-right next pro-closest-shadow' type='submit' disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>

        <div className='clearfix' />
      </form>
    </div>
  )
}

export function EmailOnlyModal (): React.ReactElement {
  return (
    <div className='pro-modal-overlay'>
      <div className='pro-modal'>
        <EmailOnlyForm />
      </div>
    </div>
  )
}
